import apiClient from './api.client';
import type { ApiResponse, Assistant, CreateAssistantRequest, UpdateAssistantRequest } from '@/types/api.types';

export const listAssistants = async (): Promise<Assistant[]> => {
  const response = await apiClient.get<ApiResponse<{ assistants: Assistant[] }>>('/admin/assistants');
  if (!response.data.data) throw new Error(response.data.error || 'Failed to fetch assistants');
  return response.data.data.assistants;
};

export const getAssistant = async (assistantId: string): Promise<Assistant> => {
  const response = await apiClient.get<ApiResponse<{ assistant: Assistant }>>(`/admin/assistants/${assistantId}`);
  if (!response.data.data) throw new Error(response.data.error || 'Failed to fetch assistant');
  return response.data.data.assistant;
};

export const createAssistant = async (data: CreateAssistantRequest): Promise<Assistant> => {
  const response = await apiClient.post<ApiResponse<{ assistant: Assistant }>>('/admin/assistants', data);
  if (!response.data.data) throw new Error(response.data.error || 'Failed to create assistant');
  return response.data.data.assistant;
};

export const updateAssistant = async (assistantId: string, data: UpdateAssistantRequest): Promise<Assistant> => {
  const response = await apiClient.patch<ApiResponse<{ assistant: Assistant }>>(`/admin/assistants/${assistantId}`, data);
  if (!response.data.data) throw new Error(response.data.error || 'Failed to update assistant');
  return response.data.data.assistant;
};

export const deleteAssistant = async (assistantId: string): Promise<void> => {
  await apiClient.delete(`/admin/assistants/${assistantId}`);
};

export const testAssistant = async (assistantId: string, prompt: string): Promise<{ response: string; model: string }> => {
  const response = await apiClient.post<ApiResponse<{ response: string; model: string }>>(`/admin/assistants/${assistantId}/test`, { prompt });
  if (!response.data.data) throw new Error(response.data.error || 'Failed to test assistant');
  return response.data.data;
};

export const listEnabledAssistants = async (): Promise<Assistant[]> => {
  const response = await apiClient.get<ApiResponse<{ assistants: Assistant[] }>>('/assistants');
  if (!response.data.data) throw new Error(response.data.error || 'Failed to fetch assistants');
  return response.data.data.assistants;
};

export const getAISettings = async (): Promise<{ hasApiKey: boolean; provider: string }> => {
  const response = await apiClient.get<ApiResponse<{ hasApiKey: boolean; provider: string }>>('/admin/assistants/ai/settings');
  if (!response.data.data) throw new Error(response.data.error || 'Failed to fetch AI settings');
  return response.data.data;
};

export const updateAISettings = async (apiKey: string): Promise<void> => {
  await apiClient.put('/admin/assistants/ai/settings', { apiKey });
};

export const testAIConnection = async (): Promise<{ success: boolean; message: string; model?: string }> => {
  const response = await apiClient.post<ApiResponse<{ success: boolean; message: string; model?: string }>>('/admin/assistants/ai/test');
  if (!response.data.data) throw new Error(response.data.error || 'Failed to test AI connection');
  return response.data.data;
};
